import { useQuery } from '@tanstack/react-query';
import { customerService, type Customer } from '@/services/customer.service';
import { equipmentModelService } from '@/services/equipment-model.service';
import { inspectionService, type Inspection } from '@/services/inspection.service';

/**
 * Every server read the intake screens share, in one place.
 *
 * Before this module the intake page and the Ops workspace each declared the
 * same three queries independently, and the equipment-model lookup ran under
 * two different keys — the same request, cached twice, refetched twice.
 *
 * TENANT ISOLATION IS UNCHANGED BY THIS FILE. Nothing here talks to the
 * database. Every function calls the same service → apiClient → REST endpoint
 * path the two pages already used, so each request still carries the caller's
 * JWT and is resolved server-side through TenantContext → the Prisma extension
 * → PostgreSQL RLS. There is no raw SQL, no direct Prisma access and no new
 * endpoint.
 *
 * CACHE KEYS AND TENANCY. React Query caches live in a single browser tab, and
 * that tab holds exactly one tenant session: `clearAuth()` on logout tears down
 * the query cache along with it, and a tenant switch is a fresh login. The keys
 * below are therefore scoped by the ids they fetch, not by tenant — the same
 * convention every other feature in this app already uses. Adding a tenant
 * segment would not increase isolation (the server ignores client-supplied
 * tenancy entirely) and would silently diverge from the keys other screens
 * invalidate against, breaking cross-screen refresh.
 */

export const intakeQueryKeys = {
  customer: (customerId: string) => ['customer-resolve', customerId] as const,
  locations: (customerId: string) => ['customer-locations', customerId] as const,
  contacts: (customerId: string, locationId: string) =>
    ['customer-location-contacts', customerId, locationId] as const,
  /** ONE key for the model lookup. It used to be two — `…-for-intake` on the
   * intake page and `…-for-intake-workspace` on the workspace — which meant the
   * identical request was fetched and stored twice. */
  equipmentModel: (modelId: string) => ['equipment-model-for-intake', modelId] as const,
  /** The intake's inspection: the source of truth for engineer, priority,
   * warranty and workflow status. */
  inspection: (intakeId: string) => ['intake-inspection', intakeId] as const,
} as const;

export function useIntakeCustomer(customerId: string, enabled = true) {
  return useQuery({
    queryKey: intakeQueryKeys.customer(customerId),
    queryFn: () => customerService.get(customerId),
    enabled: enabled && !!customerId,
  });
}

export function useIntakeCustomerLocations(customerId: string, enabled = true) {
  return useQuery({
    queryKey: intakeQueryKeys.locations(customerId),
    queryFn: () => customerService.listLocations(customerId),
    enabled: enabled && !!customerId,
  });
}

export function useIntakeCustomerContacts(customerId: string, locationId: string, enabled = true) {
  return useQuery({
    queryKey: intakeQueryKeys.contacts(customerId, locationId),
    queryFn: () => customerService.listLocationContacts(customerId, locationId),
    enabled: enabled && !!customerId && !!locationId,
  });
}

export function useIntakeEquipmentModel(modelId: string | null, enabled = true) {
  return useQuery({
    queryKey: intakeQueryKeys.equipmentModel(modelId ?? ''),
    queryFn: () => equipmentModelService.get(modelId!),
    enabled: enabled && !!modelId,
  });
}

/**
 * The intake's inspection.
 *
 * `enabled` must be driven by the caller's `inspection:read` permission: the
 * inspection module is gated independently of `intake:view`, and firing a
 * request only to swallow a 403 is noise in the server log and the network tab.
 * The server remains the authority either way.
 */
export function useIntakeInspection(intakeId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: intakeQueryKeys.inspection(intakeId ?? ''),
    queryFn: () => inspectionService.list({ intakeId: intakeId!, pageSize: 1 }),
    enabled: enabled && !!intakeId,
  });
}

/** First (and, per intake, only) inspection, or null. */
export function firstInspection(data: { data?: Inspection[] } | undefined): Inspection | null {
  return data?.data?.[0] ?? null;
}

export type { Customer };
